import { getTimeBreakdownStrings, padLeadingZero } from '@automata/ui';
import {
  ParameterDefinition,
  PublishBodyParameters,
} from '@automata/wfb-api/src/apis/wfbApiSchemas';
import { useCallback, useState } from 'react';

import { selectAppliedParameterDefinitions } from '../features/workflow/selectors';
import { useAppSelector } from '../hooks';

export const useParametersForm = () => {
  const savedParameters = useAppSelector(selectAppliedParameterDefinitions);
  const [parameters, setParameters] =
    useState<ParameterDefinition[]>(savedParameters);
  const [parametersValidity, setParametersValidity] = useState<
    Record<string, boolean>
  >({});

  const isParametersValid = Object.values(parametersValidity).every(Boolean);
  const parametersWithoutFileType = parameters.filter(
    (p) => p.type !== 'file_ref'
  );

  const handleSaveParameter = useCallback(
    (parameter: ParameterDefinition, isValid: boolean) => {
      setParametersValidity((prev) => ({
        ...prev,
        [parameter.id]: isValid,
      }));
      setParameters((prev) =>
        prev.map((p) => (p.id === parameter.id ? parameter : p))
      );
    },
    []
  );

  return {
    isParametersValid,
    parameters,
    parametersWithoutFileType,
    handleSaveParameter,
  };
};

export const getParameterValueStr = (
  parameter: ParameterDefinition
): string => {
  if (parameter.id.endsWith('_duration')) {
    const { hours, minutes, seconds } = getTimeBreakdownStrings(
      typeof parameter.default === 'number' ? parameter.default : undefined
    );

    return `[ ${padLeadingZero(hours)}:${padLeadingZero(
      minutes
    )}:${padLeadingZero(seconds)} ]`;
  }

  return `[ ${parameter.default} ]`;
};

export const getParameterValues = (
  parameters: ParameterDefinition[]
): PublishBodyParameters['parameter_values'] => {
  return parameters.map((parameter) => {
    switch (parameter.type) {
      case 'boolean':
        return {
          id: parameter.id,
          type: parameter.type,
          value: !!parameter.default,
        };
      case 'file_ref':
        return {
          id: parameter.id,
          type: parameter.type,
          name: parameter.default ?? '', // This field will store file name.
          value: '', // This field will store file content. We don't have it yet.
        };
      case 'float':
      case 'integer':
        return {
          id: parameter.id,
          type: parameter.type,
          value: parameter.default ?? 0,
        };
      case 'string':
        return {
          id: parameter.id,
          type: parameter.type,
          value: parameter.default ?? '',
        };
    }
  });
};
